import type { ReactNode } from 'react'
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import { useAppConfig } from '@/app/AppConfigGate'

import { createSupportChat } from '../lib/supportChat'
import { SupportChatContext, type SupportChat } from '../lib/useSupportChat'

/**
 * The Goftino chat (backend-routes.md §19) for the whole app.
 *
 * Mounted inside `AppConfigGate` and around the router: it needs the config,
 * not the router, and every screen that opens the chat or shows its unread
 * dot sits under it. The chat outlives route changes, and the lifecycle in
 * `lib/supportChat.ts` follows the token store rather than any screen.
 *
 * `available` is the flag and its widget together: either alone would be a
 * button that loads nothing.
 */
export function SupportChatProvider({ children }: { children: ReactNode }) {
  const config = useAppConfig()
  const [chat] = useState(createSupportChat)
  const available = config.features.support_chat && config.support_chat !== null
  const guestWidgetId = config.support_chat?.widget_id ?? null

  useEffect(() => chat.start(), [chat])
  useEffect(() => {
    chat.configure({ available, guestWidgetId })
  }, [chat, available, guestWidgetId])

  const { opening, unread, error, panelOpen } = useSyncExternalStore(
    chat.subscribe,
    chat.getSnapshot,
  )

  const value = useMemo<SupportChat>(
    () => ({ available, open: chat.open, opening, unread, error, panelOpen }),
    [available, chat, opening, unread, error, panelOpen],
  )

  return <SupportChatContext value={value}>{children}</SupportChatContext>
}
